import useJSnackbar from '@/hooks/useJSnackbar';
import Paths from '@/routes/paths';
import { JButton } from '@atoms/JButton';
import Jpaper from '@atoms/JPaper';
import { Loading } from '@atoms/Loading';
import { IconSize } from '@constants';
import useEstablishmentMutation from '@establishment/hooks/useEstablishmentMutation';
import {
  AddressDto,
  EstablishmentDto,
} from '@establishment/models/dto/establishment-mutation.dto';
import {
  EstablishmentAddressFormDto,
  EstablishmentDetailsFormDto,
  EstablishmentVideoFormDto,
} from '@establishment/models/establishment-form';
import {
  useCreateEstablishmentRequest,
  useEditEstablishmenRequest,
} from '@establishment/mutations/establishmentMutations';
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

type valuesProp =
  | EstablishmentDetailsFormDto
  | EstablishmentAddressFormDto
  | EstablishmentVideoFormDto;

interface Props {
  isValid: boolean;
  values: valuesProp;
  submitTab?: boolean;
}

const EstablishmentFormFooter = ({ isValid, values, submitTab }: Props) => {
  const { t } = useTranslation();
  const {
    mutate: createEstablishmentMutation,
    isLoading: isCreateLoading,
    isSuccess: isCreateSuccess,
    isError: isCreateError,
  } = useCreateEstablishmentRequest();
  const {
    mutate: editEstablishmentMutation,
    isLoading: isEditLoading,
    isSuccess: isEditSuccess,
    isError: isEditError,
  } = useEditEstablishmenRequest();

  const navigation = useNavigate();
  const { showSnackbar } = useJSnackbar();
  const { setStepIndex, stepIndex, setPayload, payload, mode } =
    useEstablishmentMutation();

  const setDtoValues = async (payloadSub: valuesProp) => {
    if (mode === 'edit') {
      setPayload((prev) => {
        return {
          ...prev,
          id: payload?.id,
        };
      });
    }

    // siren is part of details dto
    if ('siret' in payloadSub) {
      setPayload((prev) => {
        return {
          addressDto: prev?.addressDto,
          establishmentDto: {
            activityAreaIds: payloadSub.activityAreaIds,
            name: payloadSub.name,
            siret: payloadSub.siret,
            enterpriseId: payloadSub.enterpriseId,
            email: payloadSub.email,
            mobilePhone: payloadSub.mobilePhone,
            phone: payloadSub.phone,
            creditsAlertThreshold: payloadSub.creditsAlertThreshold,
          },
          video: prev?.video,
          thumbnail: prev?.thumbnail,
          thumbnailUrl: prev?.thumbnailUrl,
          videoUrl: prev?.videoUrl,
          id: prev?.id,
        };
      });
    }
    // street is part of address dto
    if ('street' in payloadSub) {
      setPayload((prev) => {
        if (!prev) return prev;
        return {
          establishmentDto: prev?.establishmentDto,
          addressDto: payloadSub,
          thumbnail: prev.thumbnail,
          video: prev.video,
          thumbnailUrl: prev.thumbnailUrl,
          videoUrl: prev.videoUrl,
          id: prev?.id,
        };
      });
    }
  };

  const onNextClick = async (formValues: valuesProp) => {
    await setDtoValues(formValues);
    if (!submitTab) setStepIndex((prev) => (prev ? prev + 1 : 1));
    else if (submitTab) {
      handleSubmit();
    }
  };
  const onPreviousClick = async (formValues: valuesProp) => {
    await setDtoValues(formValues);
    setStepIndex((prev) => (prev ? prev + -1 : 1));
  };

  const handleSubmit = async () => {
    const formData = new FormData();
    // Append the fields from establishmentDto
    if (payload?.establishmentDto) {
      const establishmentDto = payload.establishmentDto;
      for (const key in establishmentDto) {
        if (Object.hasOwn(establishmentDto, key)) {
          const value = getestablishmentDtoValue(establishmentDto, key);
          if (Array.isArray(value)) {
            if (value.length === 1) {
              formData.append(`establishmentDto[${key}][]`, value[0]);
            } else {
              value.forEach((item) =>
                formData.append(`establishmentDto[${key}]`, item),
              );
            }
          } else if (value !== undefined && value !== null) {
            formData.append(`establishmentDto[${key}]`, value);
          }
        }
      }
    }

    // Append the fields from addressDto
    if (payload?.addressDto) {
      const addressDto = payload.addressDto;
      for (const key in addressDto) {
        if (Object.hasOwn(addressDto, key)) {
          const value = getAddressDtoValue(addressDto, key);
          if (!(value === undefined || value === null))
            formData.append(`addressDto[${key}]`, value);
        }
      }
    }

    if (payload?.video) {
      formData.delete('videoUrl');
      formData.append('video', payload?.video);
    }
    if (payload?.thumbnail) {
      formData.delete('thumbnailUrl');
      formData.append('thumbnail', payload?.thumbnail);
    }

    try {
      // Perform  API request using the FormData object
      mode === 'create'
        ? createEstablishmentMutation({ formData })
        : payload?.id &&
          editEstablishmentMutation({ formData, id: payload?.id });
    } catch (error) {
      showSnackbar(t('API_ERROR'), 'error');
      console.error(`error while ${error} establishment`, error);
    }
  };

  const getestablishmentDtoValue = (
    data: EstablishmentDto,
    key: string,
  ): string | string[] | undefined | Blob | null => {
    const value = data[key as keyof EstablishmentDto];

    if (value !== undefined) {
      if (typeof value === 'string') {
        // Remove quotes and trim if the value is a string
        return value.replace(/"/g, '').trim();
      } else {
        return value;
      }
    }
  };

  const getAddressDtoValue = (
    data: AddressDto,
    key: string,
  ): string | undefined | Blob => {
    const value = data[key as keyof AddressDto];

    if (value !== undefined) {
      if (typeof value === 'string') {
        // Remove quotes and trim if the value is a string
        return value.replace(/"/g, '').trim();
      } else {
        return value;
      }
    }
  };

  useEffect(() => {
    if (isCreateSuccess) {
      showSnackbar(t('ESTABLISHMENT_CREATE_SUCCESS'), 'success');
      navigation(Paths.EstablishmentPage);
    }
  }, [isCreateSuccess]);
  const queryClient = useQueryClient();
  useEffect(() => {
    if (isEditSuccess) {
      queryClient.invalidateQueries(['estabilshment', payload?.id]);
      showSnackbar(t('ESTABLISHMENT_EDIT_SUCCESS'), 'success');
      navigation(Paths.EstablishmentPage);
    }
  }, [isEditSuccess]);

  useEffect(() => {
    if (isCreateError) {
      showSnackbar(t('API_ERROR'), 'error');
    }
  }, [isCreateError]);

  useEffect(() => {
    if (isEditError) {
      showSnackbar(t('API_ERROR'), 'error');
    }
  }, [isEditError]);
  if (isCreateLoading || isEditLoading) return <Loading />;

  function getButtonMessage(
    isSubmit: boolean | undefined,
    submitMode: 'create' | 'edit' | undefined,
  ) {
    if (isSubmit) {
      if (submitMode === 'create') {
        return t('CREATE_ESTABLISHMENT');
      } else if (submitMode === 'edit') {
        return t('EDIT_ESTABLISHMENT');
      }
    }
    return t('NEXT');
  }
  return (
    <Jpaper direction="row" justifyContent="space-between" alignItems="center">
      {stepIndex ? (
        <JButton
          disabled={isCreateLoading || isEditLoading}
          onClick={() => onPreviousClick(values)}
          startIcon={<ArrowLeftIcon height={IconSize.md} />}
          variant="text"
        >
          {t('PREVIOUS')}
        </JButton>
      ) : (
        <span></span>
      )}
      <JButton
        color={submitTab ? 'secondary' : 'primary'}
        onClick={() => onNextClick(values)}
        endIcon={<ArrowRightIcon height={IconSize.md} />}
        variant="contained"
        disabled={!isValid || isCreateLoading || isEditLoading}
      >
        {getButtonMessage(submitTab, mode)}
      </JButton>
    </Jpaper>
  );
};

export default EstablishmentFormFooter;
